import store from "../../../../../../../../store";
import {setSQFTActiveShapeId} from "../../../../../../../../actions/sqftMenuFunctions";
import {
  CABINET_BOXES,
  SQFT_PARENT_FEATURE_GROUP_PART_IDS,
} from "../../../../../../constants";
import {
  reloadCountertopsIn3DMode,
  reloadShapesIn3DMode,
} from "../../../../../../helpers/3dHelper/modelsHelper";
import {renderErrorPopup} from "../../../../../../../../api/api-popups";
import {reportNonFatalError} from "../../../../../../../../misc/errorReporting/errorReporter";
import cabinetWorkspace from "../../../../../canvas/core/cabinet/cabinetWorkspace";
import workspace from "../../../../../canvas/core/workspace";
import sqftShapeManager from "../../../../../canvas/store/sqftShapeManager";
import sqftShapeIntersectionsManager from "../../../../../canvas/store/sqftShapeIntersectionsManager";
import workspaceControllers from "../../../../../canvas/store/workspaceControllers";
import SqftStore from "../../../../../canvas/store/sqftStore";
import {is3DMode} from "../../../../NavigationHelper";
import {getCurrentLayout} from "../../../../../../../appLayout";
import {getCabinetSimilarFeatureFromAnotherColor} from "../../../../../../helpers/sqft3dArHelper";
import {getResolvedCabinetParentFeature} from "./cabinetParentFeatureResolver";

const CABINET_ADD_GAP = 0;

const getBoundsPosition = (bounds) => {
  if (!bounds) {
    return null;
  }

  return {
    x: bounds.right + CABINET_ADD_GAP,
    y: bounds.top
  };
};

const getShapesBounds = (shapes) => {
  const points = sqftShapeManager.getMinMaxShapesPointsOnWorkspace(shapes, true);

  if (!points.length) {
    return null;
  }

  return {
    left: points[0].x,
    top: points[0].y,
    right: points[2].x,
    bottom: points[2].y
  };
};

const getCabinetPlacementPosition = (partId) => {
  const {sqftMenuState} = store.getState();
  const activeShape = workspace.getShapeById(sqftMenuState.sqftActiveShapeId);

  if (activeShape && CABINET_BOXES.includes(activeShape.partId)) {
    return getBoundsPosition(sqftShapeManager.getShapeBoundsOnWorkspace(activeShape, true));
  }

  const cabinetShapes = workspace.getShapes()
    .filter(shape => CABINET_BOXES.includes(shape.partId));

  const samePartCabinetShapes = cabinetShapes.filter(shape => shape.partId === partId);
  const placementTargets = samePartCabinetShapes.length ? samePartCabinetShapes : cabinetShapes;

  return getBoundsPosition(getShapesBounds(placementTargets));
};

const showUnavailableCabinetModelPopup = () => {
  renderErrorPopup('messageBox.cabinet3dModelUnavailable');
};

const removeFailedCabinetShape = (shape) => {
  if (!shape) {
    return;
  }

  sqftShapeManager.removeShapeById(shape.id);
  sqftShapeIntersectionsManager.updateShapesIntersections();
  workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
  SqftStore.updateCalculations();
};

export const getCurrentCabinetParentFeature = (feature, parentFeature) => {
  const currentParentPartId = SQFT_PARENT_FEATURE_GROUP_PART_IDS[feature?.partId] || parentFeature?.partId;

  if (!currentParentPartId) {
    return parentFeature;
  }

  const featureGroup = getCurrentLayout().getFeatureGroupByPartId(currentParentPartId);
  const currentParentFeature = featureGroup?.activeFeature || parentFeature;

  return getResolvedCabinetParentFeature({
    feature,
    parentFeature: currentParentFeature,
    partId: currentParentPartId,
  });
};

export const getCurrentCabinetShapeFeature = (feature, parentFeature) => {
  const relatedFeatures = parentFeature?.getFeatureRelatedFeatures?.();

  if (!feature?.dbFeature || !Array.isArray(relatedFeatures) || !relatedFeatures.length) {
    return feature;
  }

  const isFeatureRelatedToCurrentParent = relatedFeatures.some(relatedFeature =>
    relatedFeature.id === feature.dbFeature.id
  );

  if (isFeatureRelatedToCurrentParent) {
    return feature;
  }

  const currentDbFeature = getCabinetSimilarFeatureFromAnotherColor(relatedFeatures, feature.dbFeature);

  if (!currentDbFeature?.id) {
    return feature;
  }

  return getCurrentLayout().getFeatureById(currentDbFeature.id, true) || feature;
};

// In 3D, cabinet clicks should add near the current cabinet run instead of the hidden 2D viewport center.
export const addCabinetShapeToWorkspace = async (feature, parentFeature, options = {}) => {
  const currentParentFeature = getCurrentCabinetParentFeature(feature, parentFeature);
  const currentFeature = getCurrentCabinetShapeFeature(feature, currentParentFeature);

  if (!currentFeature || !currentParentFeature) {
    return null;
  }

  const isThreeDMode = is3DMode();
  const position = options.position !== undefined
    ? options.position
    : isThreeDMode
    ? getCabinetPlacementPosition(currentFeature.partId)
    : null;
  const shapeId = cabinetWorkspace.cabinetShapes.addShape(currentFeature, currentParentFeature, position);
  const shape = workspace.getShapeById(shapeId);

  if (!shape) {
    return null;
  }

  // Active shape id drives the selected cabinet state for menus and edit controls.
  const shouldActivateShape = options.shouldActivateShape !== undefined
    ? options.shouldActivateShape
    : !isThreeDMode;

  if (shouldActivateShape) {
    store.dispatch(setSQFTActiveShapeId(shapeId));
  }

  if (isThreeDMode && options.shouldReloadIn3D !== false) {
    try {
      await reloadShapesIn3DMode([shape]);
      await reloadCountertopsIn3DMode([shape]);
    } catch (error) {
      reportNonFatalError('sqft-3d-cabinet-click-add-model-unavailable', error, {
        shapeId,
        featureId: currentFeature.id,
        parentFeatureId: currentParentFeature.id,
      });
      removeFailedCabinetShape(shape);
      showUnavailableCabinetModelPopup();
      return null;
    }
  }

  return shape;
};
